<template>
    <div
        :class="{ popover: !!position, [`popover-${position}`]: true, active: picking }"
        class="base-date-input hover-disabled"
    >
        <input-group
            glyph="calendar"
            glyph-position="right"
            @click="click_icon"
        >
            <template v-slot:left_addon><slot name="left_addon"></slot></template>
            <base-input
                ref="field"
                :id="identifier"
                type="text"
                :name="name"
                :autocomplete="name"
                :placeholder="placeholder"
                :value="display_value"
                :size="size"
                :aria-describedby="!!has_slot('hint')?`${identifier}-description`:false"
                :disabled="disabled"
                :required="required"
                @focus="focused"
                @touchstart="touchstart"
                @keyup="$emit('keyup', $event)"
                @keydown="keydown"
                @blur="blurred"
            />
        </input-group>
        <div
            v-if="!!position && picking"
            ref="calendar"
            class="popover-container"
        >
            <base-calendar
                :min="min"
                :max="max"
                :selection="value"
                :size="calendarSize || size"
                @click="pick"
            />
        </div>
    </div>
</template>

<script>
import debounce from "lodash/debounce"

import is_nibnut_component from "@/nibnut/mixins/IsNibnutComponent"
import is_alpha_numerical_input from "@/nibnut/mixins/IsAlphaNumericalInput"
import handles_dates from "@/nibnut/mixins/HandlesDates"

import InputGroup from "@/nibnut/components/Inputs/InputGroup"
import BaseInput from "@/nibnut/components/Inputs/BaseInput"
import BaseCalendar from "@/nibnut/components/Inputs/BaseCalendar"

const chrono = require("chrono-node")

export default {
    name: "BaseDateInput",
    mixins: [is_nibnut_component, is_alpha_numerical_input, handles_dates],
    components: {
        InputGroup,
        BaseInput,
        BaseCalendar
    },
    watch: {
        editable: "maybe_focus_field"
    },
    methods: {
        focus_field () {
            this.$refs.field.$el.focus()
            // this.$refs.field.$el.select()
        },
        maybe_focus_field () {
            if(this.editable) setTimeout(this.focus_field, 150)
        },
        focused () {
            this.$refs.field.$el.select()
            this.picking = true
            this.$emit("focus")
        },
        keydown: debounce(function () {
            if(this.alphanumeric && this.iOS) this.touching = false
        }, 300),
        blurred (event) {
            /* THE SAFARI MESS:
            Click on calendar (anything: nav arrows OR actual date!) will issue a blur event first, with relatedTarget === null
            Click outside calendar outside a field will issue a blur event first, with relatedTarget === null
            Tab out of field will issue a blur event first, with relatedTarget !== null
            Click outside calendar inside a field will issue a blur event first, with relatedTarget !== null
            SO:
            Handle clicks on document: if event inside calendar, ignore, else stop picking
            For blur, if there is a relatedTarget, analyze the target's value
            */
            if(this.alphanumeric && this.iOS) this.touching = false

            const go_on = () => {
                let selected_date = null
                try {
                    if(event.target.value) {
                        if(event.target.value.match(/^\d{1,4}.+\d{1,4}.+\d{1,4}$/)) {
                            selected_date = this.$moment(event.target.value)
                        } else {
                            selected_date = chrono.parseDate(event.target.value)
                            if(isNaN(selected_date.getTime())) selected_date = null
                        }
                    }
                } catch (error) {
                    selected_date = null
                }
                if(!selected_date || (this.$moment(this.standardized_date(this.value)).format(this.dateFormat) !== this.$moment(selected_date).format(this.dateFormat))) this.$emit("input", selected_date, this.name)
                this.picking = false
            }

            if(this.is_safari && !!event.relatedTarget) go_on()
            else if(!this.is_safari) {
                const is_calendar_clicked = !!event.relatedTarget && !!this.$refs.calendar && this.$refs.calendar.contains(event.relatedTarget)
                if(!is_calendar_clicked) go_on()
            }
            this.$emit("blur")
        },
        click_icon () {
            this.focus_field()
        },
        pick (date) {
            this.$emit("input", date, this.name)
            if(this.closeOnClick) this.picking = false
        }
    },
    computed: {
        display_value () {
            if(!this.value) return ""
            return this.standardized_date(this.value).format(this.dateFormat)
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        placeholder: {
            type: String,
            default: ""
        },
        value: {
            default: ""
        },
        dateFormat: {
            type: String,
            default: "MM/DD/YYYY"
        },
        position: { // use "over" for dialogs, where the popup spills out: "over" will place the calendar popup "over" the field/trigger
            validator: prop => {
                return !prop || (typeof prop === "string") || !!prop.match(/^(top|bottom|left|right|over)$/i)
            },
            default: "top"
        },
        min: { // null, string or native date
            default: null
        },
        max: { // null, string or native date
            default: null
        },
        size: {
            type: String,
            validator: prop => !!prop && prop.match(/^(sm|md|lg)$/i),
            default: "md"
        },
        calendarSize: {
            type: String,
            validator: prop => !prop || prop.match(/^(sm|md|lg)$/i),
            default: ""
        },
        closeOnClick: {
            type: Boolean,
            default: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        required: {
            type: Boolean,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ""
        }
    },
    data () {
        return {
            picking: false
        }
    }
}
</script>

<style lang="scss">
.base-date-input {
    width: 100%;
}
</style>
